import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../auth/authStore'
import { getPlanningRating } from '../../../shared/api/planning.api'
import { currentMonthKey } from '@crm/shared'
import type { PlanningRatingRow } from '@crm/shared'
import type { DashboardPeriodKey } from './types'

interface Props {
  periodKey: DashboardPeriodKey
  customFrom: string | null
  customTo: string | null
  /** financial — план/факт в ₽; akb — план/факт количества клиентов. */
  kind: 'financial' | 'akb'
}

function getSelection(user: { gridSettings?: Record<string, unknown> | null } | null): string[] {
  const gs = user?.gridSettings as Record<string, unknown> | null | undefined
  const sel = gs?.planningSelection
  if (Array.isArray(sel)) return sel.filter((x): x is string => typeof x === 'string')
  return []
}

function buildQuery(
  periodKey: DashboardPeriodKey,
  customFrom: string | null,
  customTo: string | null,
): {
  period: 'month' | 'quarter' | 'year' | 'custom'
  date?: string
  from?: string
  to?: string
} | null {
  const now = new Date()
  if (periodKey === 'month') return { period: 'month', date: currentMonthKey(now) }
  if (periodKey === 'quarter') {
    const y = now.getUTCFullYear()
    const qm = Math.floor(now.getUTCMonth() / 3) * 3 + 1
    return { period: 'quarter', date: `${y}-${String(qm).padStart(2, '0')}` }
  }
  if (periodKey === 'year') return { period: 'year', date: String(now.getUTCFullYear()) }
  if (periodKey === 'custom' && customFrom && customTo) {
    return { period: 'custom', from: customFrom, to: customTo }
  }
  return null
}

// Деньги в компактном «M»-формате: 0.65M, 12.4M.
function formatMoney(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000_000).toFixed(2)}M`
  return `${n}`
}

function percentColor(pct: number): { bar: string; text: string } {
  if (pct >= 90) return { bar: '#16a34a', text: 'text-emerald-600' }
  if (pct >= 70) return { bar: '#2563eb', text: 'text-blue-600' }
  if (pct >= 45) return { bar: '#ea580c', text: 'text-orange-600' }
  return { bar: '#dc2626', text: 'text-red-600' }
}

export function PlanFactRating({ periodKey, customFrom, customTo, kind }: Props) {
  const user = useAuthStore((s) => s.user)
  const selection = useMemo(() => getSelection(user), [user])
  const query = useMemo(
    () => buildQuery(periodKey, customFrom, customTo),
    [periodKey, customFrom, customTo],
  )

  const enabled = Boolean(query)
  const { data, isLoading } = useQuery({
    queryKey: ['planning-rating', query, selection],
    queryFn: () => getPlanningRating({ ...query!, managerIds: selection }),
    enabled,
  })

  const title = kind === 'financial' ? 'Рейтинг план / факт' : 'Рейтинг АКБ план / факт'

  const rows = useMemo(() => {
    if (!data) return []
    const pickPlan = (r: PlanningRatingRow) =>
      kind === 'financial' ? r.planAmount : r.akbPlanCount
    const pickFact = (r: PlanningRatingRow) =>
      kind === 'financial' ? r.factAmount : r.akbFactCount
    // Сортировка по % выполнения (убывание). Строки без плана — в конец.
    return [...data.rows]
      .map((r) => {
        const plan = pickPlan(r)
        const fact = pickFact(r)
        const pct = plan > 0 ? Math.round((fact / plan) * 100) : -1
        return { row: r, plan, fact, pct }
      })
      .sort((a, b) => b.pct - a.pct)
  }, [data, kind])

  const withPlan = rows.filter((r) => r.pct >= 0)
  const noPlan = rows.filter((r) => r.pct < 0)

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col gap-2">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="text-sm font-medium text-gray-700">{title}</div>
        {withPlan.length > 0 && (
          <div className="text-xs text-gray-400">
            {withPlan.length} с планом, отсортированы по % выполнения
          </div>
        )}
      </div>

      {!query ? (
        <Empty>Рейтинг доступен для периодов «Месяц», «Квартал», «Год» и «Период…».</Empty>
      ) : isLoading || !data ? (
        <Empty>Загрузка…</Empty>
      ) : withPlan.length === 0 ? (
        <Empty>
          Ни у кого не задан план. Заполните план на странице{' '}
          <Link to="/planning" className="text-brand-600 hover:underline">
            Планирование
          </Link>
          .
        </Empty>
      ) : (
        <div className="flex flex-col">
          {withPlan.map((item, idx) => (
            <RatingBar key={item.row.unitId} index={idx + 1} item={item} kind={kind} />
          ))}
          {noPlan.length > 0 && (
            <div className="text-xs text-gray-400 mt-2">
              Без плана: {noPlan.map((r) => r.row.label).join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-gray-500 px-2 py-6 text-center">{children}</div>
}

interface BarItem {
  row: PlanningRatingRow
  plan: number
  fact: number
  pct: number
}

function RatingBar({
  index,
  item,
  kind,
}: {
  index: number
  item: BarItem
  kind: 'financial' | 'akb'
}) {
  const { row, plan, fact, pct } = item
  const color = percentColor(pct)
  const fillPercent = Math.min(pct, 100)
  const fmt = (n: number) => (kind === 'financial' ? formatMoney(n) : String(n))

  return (
    <div className="flex items-center gap-3 py-1.5 border-b border-gray-50 last:border-0">
      <span className="w-5 text-xs text-gray-400 tabular-nums text-right">{index}</span>
      <span className="w-44 text-sm text-gray-800 truncate" title={row.label}>
        {row.label}
      </span>
      <div className="flex-1 h-5 bg-gray-100 rounded-md overflow-hidden relative">
        <div
          className="h-full rounded-md transition-all"
          style={{ width: `${fillPercent}%`, background: color.bar }}
        />
      </div>
      <span className="w-28 text-xs text-gray-500 tabular-nums text-right">
        {fmt(plan)} / {fmt(fact)}
      </span>
      <span className={`w-14 text-sm font-semibold tabular-nums text-right ${color.text}`}>
        {pct}%
      </span>
      <span className="w-4 text-xs">{pct >= 90 ? '✓' : pct < 45 ? '⚠' : ''}</span>
    </div>
  )
}
